import {
  DEFAULT_OFFLINE_CAP_HOURS,
  FULL_RATE_MS,
  LEVEL_CAP,
  OFFLINE_EFFICIENCY_AFTER,
  type IdleRates,
  type Item,
  type PityState,
  type ProgressState,
  type SaveData,
} from '../model/types';
import { grantXp, xpFalloffHundredths, xpToNext } from './build';
import { absorbDrops } from './gear';

export interface OfflineGains {
  /** Clamped elapsed time the claim used. */
  elapsedMs: number;
  /** Elapsed before the cap, useful for the "capped" line. */
  rawElapsedMs: number;
  /** Time after the 100% / 75% curve. */
  effectiveMs: number;
  capped: boolean;
  gold: number;
  xp: number;
  dust: number;
  dropRolls: number;
  items: Item[];
  pity: PityState;
  farmStageId: number;
  /** Offline never pushes. This stays 0 so callers cannot miss it. */
  stagesPushed: 0;
}

export interface OfflineInput {
  rates: IdleRates;
  farmStageId: number;
  /** When set, XP uses the same level-by-level falloff as claim_offline. Gold and dust do not. */
  heroLevel?: number;
  /** XP already in the bar. The walk levels sooner when this is set. */
  heroXp?: number;
  serverNowMs: number;
  lastActiveAtMs: number;
  /** Earned cap. Defaults to 8 hours. */
  capHours?: number;
  pity?: PityState;
  /**
   * Optional item roller. The time math does not roll items itself, so the same
   * curve can live in a Postgres function that only multiplies rates.
   */
  rollItems?: (count: number, pity: PityState) => { items: Item[]; pity: PityState };
}

export function clampElapsed(serverNowMs: number, lastActiveAtMs: number, capHours: number): {
  rawElapsedMs: number;
  elapsedMs: number;
  capped: boolean;
} {
  const rawElapsedMs = serverNowMs - lastActiveAtMs;
  if (rawElapsedMs <= 0) return { rawElapsedMs, elapsedMs: 0, capped: false };
  const capMs = capHours * 60 * 60 * 1000;
  const capped = rawElapsedMs > capMs;
  return { rawElapsedMs, elapsedMs: Math.min(rawElapsedMs, capMs), capped };
}

/** 100% for the first 2 hours, then 75% of the remaining clamped time. */
export function effectiveOfflineMs(elapsedMs: number): number {
  if (elapsedMs <= 0) return 0;
  const full = Math.min(elapsedMs, FULL_RATE_MS);
  const rest = elapsedMs - full;
  return full + rest * OFFLINE_EFFICIENCY_AFTER;
}

const IDLE_XP_DENOM = 60_000_000_000n;

export interface IdleLevelReach {
  level: number;
  /** Effective milliseconds spent in this claim when this level was reached. */
  effectiveMs: number;
}

export interface IdleXpWalk {
  level: number;
  xp: number;
  unspent: number;
  /** XP gained during the window, not the bar after the claim. */
  awarded: number;
  /** Each level crossed during this claim, in order. */
  reached: IdleLevelReach[];
}

/**
 * Spend one claim's effective time level by level.
 * Each level uses that level's falloff and `xpToNext`. Time left after the
 * cap still counts toward `awarded`; the bar stays empty at the cap.
 * Scale matches `private.walk_idle_xp`: rate in 1/10000, falloff in 1/100,
 * time in milliseconds, denominator 60e9.
 * A farm stage below 1 awards no XP.
 */
export function walkIdleXp(args: {
  level: number;
  xp: number;
  unspent: number;
  xpPerMin: number;
  stageId: number;
  effectiveSeconds: number;
  /** Pace comparisons pass the old line. The live claim uses `xpToNext`. */
  need?: (level: number) => number;
  useFalloff?: boolean;
}): IdleXpWalk {
  const need = args.need ?? xpToNext;
  const useFalloff = args.useFalloff !== false;
  let level = Math.max(1, Math.min(LEVEL_CAP, Math.floor(args.level) || 1));
  let xp = Math.max(0, Math.floor(args.xp));
  let unspent = Math.max(0, Math.floor(args.unspent));
  const empty = { level, xp, unspent, awarded: 0, reached: [] as IdleLevelReach[] };
  if (!Number.isInteger(args.stageId) || args.stageId < 1) return empty;
  if (!(args.xpPerMin > 0) || !(args.effectiveSeconds > 0)) return empty;
  const rateUnits = BigInt(Math.round(args.xpPerMin * 10_000));
  if (rateUnits <= 0n) return empty;
  let msLeft = BigInt(Math.max(0, Math.round(args.effectiveSeconds * 1000)));
  const started = msLeft;
  let awarded = 0n;
  const reached: IdleLevelReach[] = [];
  const hundredthsAt = (at: number): bigint =>
    BigInt(useFalloff ? xpFalloffHundredths(at, args.stageId) : 100);
  const mark = (): void => {
    reached.push({ level, effectiveMs: Number(started - msLeft) });
  };

  while (level < LEVEL_CAP && xp >= need(level)) {
    xp -= need(level);
    level += 1;
    unspent += 1;
    mark();
  }

  if (level >= LEVEL_CAP) {
    const gain = (rateUnits * hundredthsAt(level) * msLeft) / IDLE_XP_DENOM;
    return { level: LEVEL_CAP, xp: 0, unspent, awarded: Number(awarded + gain), reached };
  }

  while (level < LEVEL_CAP && msLeft > 0n) {
    const per = rateUnits * hundredthsAt(level);
    if (per <= 0n) break;
    const gap = need(level) - xp;
    if (gap <= 0) break;
    const gapBi = BigInt(gap);
    let msNeed = (gapBi * IDLE_XP_DENOM + per - 1n) / per;
    if (msNeed < 1n) msNeed = 1n;
    if (msNeed <= msLeft) {
      awarded += gapBi;
      msLeft -= msNeed;
      xp = 0;
      level += 1;
      unspent += 1;
      mark();
    } else {
      const gain = (per * msLeft) / IDLE_XP_DENOM;
      awarded += gain;
      xp += Number(gain);
      msLeft = 0n;
    }
  }

  if (level >= LEVEL_CAP) {
    xp = 0;
    if (msLeft > 0n) awarded += (rateUnits * hundredthsAt(level) * msLeft) / IDLE_XP_DENOM;
  }
  return { level, xp, unspent, awarded: Number(awarded), reached };
}

/**
 * Seconds form of the same curve, matching claim_offline:
 * full = least(elapsed, 7200); rest = greatest(0, elapsed - 7200); effective = full + rest * 0.75.
 */
export function offlineGainsFromSeconds(args: {
  elapsedSeconds: number;
  capHours: number;
  rates: IdleRates;
  heroLevel?: number;
  heroXp?: number;
  farmStageId?: number;
}): {
  elapsedSeconds: number;
  effectiveSeconds: number;
  capped: boolean;
  gold: number;
  xp: number;
  dust: number;
  dropRolls: number;
  stagesPushed: 0;
} {
  const raw = args.elapsedSeconds;
  if (raw <= 0) {
    return {
      elapsedSeconds: 0,
      effectiveSeconds: 0,
      capped: false,
      gold: 0,
      xp: 0,
      dust: 0,
      dropRolls: 0,
      stagesPushed: 0,
    };
  }
  const capSeconds = args.capHours * 3600;
  const capped = raw > capSeconds;
  const elapsedSeconds = Math.min(raw, capSeconds);
  const full = Math.min(elapsedSeconds, FULL_RATE_MS / 1000);
  const rest = Math.max(0, elapsedSeconds - full);
  const effectiveSeconds = full + rest * OFFLINE_EFFICIENCY_AFTER;
  const minutes = effectiveSeconds / 60;
  const xp =
    args.heroLevel === undefined
      ? Math.floor(args.rates.xpPerMin * minutes)
      : walkIdleXp({
          level: args.heroLevel,
          xp: args.heroXp ?? 0,
          unspent: 0,
          xpPerMin: args.rates.xpPerMin,
          stageId: args.farmStageId ?? 0,
          effectiveSeconds,
        }).awarded;
  return {
    elapsedSeconds,
    effectiveSeconds,
    capped,
    gold: Math.floor(args.rates.goldPerMin * minutes),
    xp,
    dust: Math.floor(args.rates.dustPerMin * minutes),
    dropRolls: Math.floor(args.rates.dropRollsPerMin * minutes),
    stagesPushed: 0,
  };
}

export function computeOfflineGains(input: OfflineInput): OfflineGains {
  const capHours = input.capHours ?? DEFAULT_OFFLINE_CAP_HOURS;
  const { rawElapsedMs, elapsedMs, capped } = clampElapsed(input.serverNowMs, input.lastActiveAtMs, capHours);
  const effectiveMs = effectiveOfflineMs(elapsedMs);
  const minutes = effectiveMs / 60000;
  const xp =
    input.heroLevel === undefined
      ? Math.floor(input.rates.xpPerMin * minutes)
      : walkIdleXp({
          level: input.heroLevel,
          xp: input.heroXp ?? 0,
          unspent: 0,
          xpPerMin: input.rates.xpPerMin,
          stageId: input.farmStageId,
          effectiveSeconds: effectiveMs / 1000,
        }).awarded;
  const gold = Math.floor(input.rates.goldPerMin * minutes);
  const dust = Math.floor(input.rates.dustPerMin * minutes);
  const dropRolls = Math.floor(input.rates.dropRollsPerMin * minutes);
  const pity = input.pity ?? { epicPlusSinceLegendary: 0 };
  const rolled = input.rollItems && dropRolls > 0 ? input.rollItems(dropRolls, pity) : { items: [], pity };
  return {
    elapsedMs,
    rawElapsedMs,
    effectiveMs,
    capped,
    gold,
    xp,
    dust,
    dropRolls,
    items: rolled.items,
    pity: rolled.pity,
    farmStageId: input.farmStageId,
    stagesPushed: 0,
  };
}

export function zeroRates(): IdleRates {
  return { goldPerMin: 0, xpPerMin: 0, dustPerMin: 0, dropRollsPerMin: 0 };
}

/** Applies a claim onto a save. Progress stages are copied through unchanged. */
export function applyOfflineToSave(data: SaveData, gains: OfflineGains): SaveData {
  const next: SaveData = structuredClone(data);
  next.currencies.gold += gains.gold;
  next.currencies.forgeDust += gains.dust;
  const leveled = grantXp(next.hero.level, next.hero.xp, next.hero.unspentPoints, gains.xp);
  next.hero.level = leveled.level;
  next.hero.xp = leveled.xp;
  next.hero.unspentPoints = leveled.unspent;
  const bag = absorbDrops(next, gains.items);
  next.gear.bag = bag.bag;
  next.currencies.gold += bag.gold;
  next.currencies.forgeDust += bag.forgeDust;
  next.gear.pity = gains.pity;
  next.progress = cloneProgress(data.progress);
  return next;
}

function cloneProgress(progress: ProgressState): ProgressState {
  return { ...progress };
}

/** Earned caps from §5.2. This slice only has the 8h base; later unlocks plug in here. */
export function earnedOfflineCapHours(): number {
  return DEFAULT_OFFLINE_CAP_HOURS;
}
